import React, { useState, useEffect, useMemo } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  BugReport as BugReportIcon,
  Public as PublicIcon,
  VpnKey as VpnKeyIcon,
  Chat as ChatIcon,
  Phone as PhoneIcon,
  SmartToy as SmartToyIcon,
  HowToReg as HowToRegIcon,
  Close as CloseIcon,
  Map as MapIcon
} from '@mui/icons-material';
import Cin7PortConfigAdmin from '../cin7-port-config/Cin7PortConfigAdmin';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../config/api';
import { brandService } from '../../services/brandService';
import DebugLogsTab from './DebugLogsTab';
import CountryMappingTab from './CountryMappingTab';
import CompanySettingsTab from './CompanySettingsTab';
import CompanyModulesTab from './CompanyModulesTab';
import { MODULES } from '../../config/modules';
import CallCentrePhoneNumbersTab from './CallCentrePhoneNumbersTab';
import AIAgentsTab from './AIAgentsTab';
import PermissionsDialog from './PermissionsDialog';

// Tab configuration with URL slugs
const SUPER_ADMIN_COMPANY_ID = 1;

const TAB_CONFIG = [
  { index: 0, slug: 'users', label: 'Users' },
  { index: 1, slug: 'company', label: 'Company' },
  { index: 2, slug: 'country-mapping', label: 'Country Mapping' },
  { index: 3, slug: 'call-centre', label: 'Call Centre' },
  { index: 4, slug: 'debug-logs', label: 'Debug Logs' },
  { index: 5, slug: 'ai-agents', label: 'AI Agents' },
  { index: 6, slug: 'registrations', label: 'Registrations' },
  { index: 7, slug: 'companies', label: 'Companies' }, // super-admin only — conditionally rendered
];

const AdminPanel = () => {
  const { confirmDialogProps, confirm } = useConfirmDialog();

  const theme = useTheme();
  const { user, permissions } = useAuth();

  // Tab visibility — super-admin sees everything; for non-super-admins,
  // Call Centre and AI Agents are gated by the user's effective permissions
  // (which already factor in the company's enabled_modules whitelist).
  const isSuperAdmin = !!user && Number(user.company_id) === SUPER_ADMIN_COMPANY_ID && !!user.isCompanyAdmin;
  const perms = Array.isArray(permissions) ? permissions : [];
  const canSeeCallCentreTab = isSuperAdmin || perms.includes(MODULES.CALL_CENTRE);
  const canSeeAIAgentsTab   = isSuperAdmin || perms.includes(MODULES.EXPERTS);
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL or default to 0
  const activeTab = useMemo(() => {
    const tabSlug = searchParams.get('tab');
    if (tabSlug) {
      const found = TAB_CONFIG.find(t => t.slug === tabSlug);
      return found ? found.index : 0;
    }
    return 0;
  }, [searchParams]);

  // Update URL when tab changes
  const setActiveTab = (newIndex) => {
    const tab = TAB_CONFIG.find(t => t.index === newIndex);
    if (tab) {
      setSearchParams({ tab: tab.slug }, { replace: true });
    }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [userForm, setUserForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  const [brandForm, setBrandForm] = useState({
    name: '',
    description: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Unified users (merged platform + Slack)
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersError, setAllUsersError] = useState(''); // last users-list load failed (DT-2012)
  const [teamId, setTeamId] = useState(null);
  const [connectorId, setConnectorId] = useState(null);
  const [hasSlack, setHasSlack] = useState(false);
  const [slackChecked, setSlackChecked] = useState(false); // slack-info lookup finished (DT-1621)

  // Slack link form
  const [searchEmail, setSearchEmail] = useState('');
  const [foundSlackUser, setFoundSlackUser] = useState(null);
  const [linkPermissionMode, setLinkPermissionMode] = useState('full_access');
  const [showLinkForm, setShowLinkForm] = useState(false);

  // Slack permissions dialog (PermissionsDialog component)
  const [slackPermDialogOpen, setSlackPermDialogOpen] = useState(false);
  const [slackPermUser, setSlackPermUser] = useState(null);

  // Hidden users
  const [hiddenUsers, setHiddenUsers] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenUsersError, setHiddenUsersError] = useState(''); // last hidden-users load failed (DT-2018)

  // Pending registrations
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState(''); // last registrations load failed (DT-2018)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectCompanyId, setRejectCompanyId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchBrands();
    fetchPendingRegistrations();
    // Fetch Slack info first, then load Slack-dependent data
    const initSlack = async () => {
      try {
        const response = await api.get('/slack/admin/slack-info');
        if (response.data.success && response.data.data) {
          const { teamId: tid, connectorId: cid } = response.data.data;
          setTeamId(tid);
          setConnectorId(cid);
          setHasSlack(true);
        }
        setSlackChecked(true); // only a real answer (data or null) may say "not connected"; an error says nothing
      } catch (err) {
        console.error('No Slack integration found:', err);
      }
    };
    initSlack();
  }, []);

  // Load Slack-dependent data once teamId is available, or fall back to platform users
  useEffect(() => {
    if (teamId) {
      fetchAllUsers();
      fetchHiddenUsers();
    } else if (!hasSlack && users.length > 0) {
      // No Slack — show platform users in the unified table format
      setAllUsers(users.map(u => ({
        id: `platform_${u.id}`,
        name: u.fullName || u.full_name || '',
        email: u.email || '',
        accessType: 'web',
        permissionMode: '',
        platformUserId: u.id,
        slackUserId: '',
        slackTeamId: '',
        role: u.role || '',
        presence: '',
        lastActivity: '',
        brandCount: 0,
        agentCount: 0,
        isCallCentreAgent: 0,
        sort_order: 0
      })));
    }
  }, [teamId, hasSlack, users]);

  // Transform snake_case to camelCase for user data
  const transformUserData = (user) => ({
    ...user,
    fullName: user.full_name,
    isCompanyAdmin: user.is_company_admin,
    createdAt: user.created_at,
    updatedAt: user.updated_at,
    companyName: user.company_name
  });

  const fetchUsers = async () => {
    try {
      setError('');
      setLoading(true);

      const response = await api.get('/api/users');
      // Transform snake_case to camelCase
      const transformedUsers = response.data.map(transformUserData);
      setUsers(transformedUsers);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      setError('');
      
      const brandsData = await brandService.getBrands();
      setBrands(brandsData);
      
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError(err.response?.data?.message || 'Failed to load brands');
    }
  };

  const fetchAllUsers = async () => {
    if (!teamId) return;
    setAllUsersError('');
    try {
      const response = await api.get(`/slack/admin/all-users/${teamId}`);
      if (response.data.success) {
        setAllUsers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching all users:', err);
      // DT-2012: a failed (re)load used to leave the list stale with no message. Shown on the Users tab and inside
      // PermissionsDialog, which stays open after a save. Fixed text: this route's 500 body carries raw error.message.
      setAllUsersError('Could not refresh the users list, so it may be out of date. Use Refresh to try again.');
    }
  };

  const handleSearchSlackUser = async () => {
    if (!searchEmail || !searchEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setFoundSlackUser(null);
      const response = await api.post('/slack/admin/get-user-id-by-email', {
        email: searchEmail,
        connectorId: connectorId
      });
      if (response.data.success) {
        setFoundSlackUser(response.data.data);
        toast.success(`Found Slack user: ${response.data.data.name}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.hint || 'User not found in Slack workspace');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSlackUser = async () => {
    if (!searchEmail || !searchEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await api.post('/slack/admin/link-by-email', {
        email: searchEmail,
        connectorId: connectorId,
        permissionMode: linkPermissionMode
      });
      if (response.data.success) {
        const data = response.data.data;
        const msg = data.slackOnly
          ? `Linked ${data.slackUser?.name || searchEmail} as Slack-only user`
          : data.autoMatched
            ? `Linked ${data.slackUser?.name || searchEmail} → ${data.platformUserName}`
            : `Successfully linked ${data.slackUser?.name || searchEmail}`;
        toast.success(msg);
        setSearchEmail('');
        setFoundSlackUser(null);
        setShowLinkForm(false);
        fetchAllUsers();
        fetchUsers(); // Refresh platform users for Edit Account lookup
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.hint || 'Failed to link user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkSlackUser = async (slackUserId) => {
    if (!await confirm({ title: 'Unlink User', message: 'Are you sure you want to unlink this Slack user?', confirmText: 'Unlink', severity: 'warning' })) return;
    try {
      setLoading(true);
      setError('');
      const response = await api.post('/slack/admin/unlink-user', {
        slackUserId: slackUserId,
        slackTeamId: teamId
      });
      if (response.data.success) {
        toast.success('User unlinked successfully');
        fetchAllUsers();
        fetchUsers(); // Refresh platform users for Edit Account lookup
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlink user');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSlackPermissions = (userData) => {
    // Build the user object PermissionsDialog expects (works for both Slack and web-only users)
    setSlackPermUser({
      slack_user_id: userData.slackUserId || null,
      slack_team_id: teamId,
      slack_display_name: userData.name,
      slack_email: userData.email,
      display_name: userData.name,
      email: userData.email,
      permission_mode: userData.permissionMode,
      scm_user_id: userData.platformUserId || 0
    });
    setSlackPermDialogOpen(true);
  };

  const fetchHiddenUsers = async () => {
    if (!teamId) return;
    setHiddenUsersError('');
    try {
      const response = await api.get(`/slack/admin/hidden-users/${teamId}`);
      if (response.data.success) {
        setHiddenUsers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching hidden users:', err);
      // DT-2012 sibling: this reload runs after Hide / Restore; its failure used to leave the list stale silently.
      // DT-2018: own state (not the shared page error), cleared as each load starts, so a later good load clears it.
      setHiddenUsersError('Could not refresh the hidden users list, so it may be out of date. Reload the page to try again.');
    }
  };

  const handleHideUser = async (slackUserId, userName) => {
    if (!await confirm({ title: 'Hide User', message: `Hide ${userName} from the Users list? They can be restored later.`, confirmText: 'Hide', severity: 'warning' })) return;
    try {
      setLoading(true);
      await api.post('/slack/admin/hide-user', { slackUserId, slackTeamId: teamId });
      toast.success(`${userName} hidden from Users list`);
      fetchAllUsers();
      fetchHiddenUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to hide user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserPermissions = async (u) => {
    if (!await confirm({ title: 'Remove Permissions', message: `Remove all permissions for ${u.name || u.email}? This will revoke all brand and agent access.`, confirmText: 'Remove All', severity: 'warning' })) return;
    try {
      setLoading(true);
      await api.post('/slack/admin/remove-user', {
        slackUserId: u.slackUserId,
        slackTeamId: teamId
      });
      toast.success(`Removed all permissions for ${u.name || u.email}`);
      fetchAllUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnhideUser = async (slackUserId, userName) => {
    try {
      setLoading(true);
      await api.post('/slack/admin/unhide-user', { slackUserId, slackTeamId: teamId });
      toast.success(`${userName} restored to Users list`);
      fetchAllUsers();
      fetchHiddenUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unhide user');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLink = async (slackUserId, email) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.post('/slack/admin/link-by-email', {
        email: email,
        connectorId: connectorId,
        permissionMode: 'restricted'
      });
      if (response.data.success) {
        const data = response.data.data;
        toast.success(`Linked ${data.slackUser?.name || email} as ${data.slackOnly ? 'Slack-only' : 'platform-linked'} user (restricted)`);
        fetchAllUsers();
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.hint || 'Failed to link user');
    } finally {
      setLoading(false);
    }
  };

  const getAccessTypeColor = (accessType) => {
    switch (accessType) {
      case 'both': return theme.palette.primary.main;
      case 'slack': return '#4A154B';
      case 'web': return theme.palette.info.main;
      case 'not_linked': return theme.palette.grey[500];
      default: return theme.palette.grey[500];
    }
  };

  const getPermissionModeColor = (mode) => {
    switch (mode) {
      case 'full_access': return theme.palette.success.main;
      case 'admin': return theme.palette.error.main;
      case 'restricted': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 6) fetchPendingRegistrations();
  };

  // --- Pending Registrations ---
  const fetchPendingRegistrations = async () => {
    setPendingLoading(true);
    setPendingError('');
    try {
      const res = await api.get('/api/users/pending-registrations');
      setPendingRegistrations(res.data.registrations || []);
    } catch (err) {
      console.error('Error fetching pending registrations:', err);
      // DT-2012 sibling: a failed reload after Approve / Reject left the list stale silently. Only super-admins can
      // read it; the page-load call answers 403 for every other admin, so that stays quiet.
      // DT-2018: own state, shown only on the Registrations tab, whose Refresh reloads this list (the Users tab's
      // Refresh does not); cleared as each load starts.
      if (isSuperAdmin) setPendingError('Could not refresh the pending registrations list, so it may be out of date. Use Refresh to try again.');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApproveCompany = async (companyId) => {
    try {
      setError('');
      await api.post('/api/users/approve-company', { companyId });
      toast.success('Company approved successfully!');
      fetchPendingRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Error approving company');
    }
  };

  const handleRejectCompany = async () => {
    if (!rejectCompanyId) return;
    try {
      setError('');
      await api.post('/api/users/reject-company', { companyId: rejectCompanyId, reason: rejectReason });
      toast.success('Company registration rejected.');
      setRejectDialogOpen(false);
      setRejectCompanyId(null);
      setRejectReason('');
      fetchPendingRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Error rejecting company');
    }
  };

  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setUserForm({
        fullName: user.fullName,
        email: user.email,
        password: '',
        confirmPassword: '',
        role: user.role
      });
    } else {
      setSelectedUser(null);
      setUserForm({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'user'
      });
    }
    setOpenUserDialog(true);
    setError('');
  };
  
  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setSelectedUser(null);
    setUserForm({
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'user'
    });
  };
  
  const handleOpenBrandDialog = (brand = null) => {
    if (brand) {
      setSelectedBrand(brand);
      setBrandForm({
        name: brand.name,
        description: brand.description || ''
      });
    } else {
      setSelectedBrand(null);
      setBrandForm({
        name: '',
        description: ''
      });
    }
    setOpenBrandDialog(true);
    setError('');
  };
  
  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setSelectedBrand(null);
    setBrandForm({
      name: '',
      description: ''
    });
  };
  
  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleBrandFormChange = (e) => {
    const { name, value } = e.target;
    setBrandForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!userForm.fullName.trim() || !userForm.email.trim()) {
      setError('Please fill out all required fields');
      return;
    }
    
    if (!selectedUser && (!userForm.password || userForm.password.length < 6)) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (!selectedUser && userForm.password !== userForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      if (selectedUser) {
        // Update existing user
        const updateData = {
          fullName: userForm.fullName,
          email: userForm.email,
          role: userForm.role
        };

        if (userForm.password) {
          updateData.password = userForm.password;
        }

        const response = await api.put(`/api/users/${selectedUser.id}`, updateData);

        // Transform response data from snake_case to camelCase
        const updatedUser = transformUserData(response.data.user);
        setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
        toast.success('User updated successfully');
      } else {
        // Create new user
        const response = await api.post('/api/users/register', {
          fullName: userForm.fullName,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role
        });

        // Transform response data from snake_case to camelCase
        const newUser = transformUserData(response.data.user);
        setUsers([...users, newUser]);
        toast.success('User created successfully');
      }
      
      handleCloseUserDialog();
      
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!await confirm({ title: 'Delete User', message: 'Are you sure you want to delete this user?', confirmText: 'Delete', severity: 'danger' })) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.delete(`/api/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');

    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateBrand = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!brandForm.name.trim()) {
      setError('Brand name is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      if (selectedBrand) {
        // Update existing brand
        const response = await brandService.updateBrand(selectedBrand.id, brandForm);
        setBrands(brands.map(b => b.id === selectedBrand.id ? response : b));
        toast.success('Brand updated successfully');
      } else {
        // Create new brand
        const response = await brandService.createBrand(brandForm);
        setBrands([...brands, response]);
        toast.success('Brand created successfully');
      }
      
      handleCloseBrandDialog();
      
    } catch (err) {
      console.error('Error saving brand:', err);
      setError(err.response?.data?.message || 'Failed to save brand');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteBrand = async (brandId) => {
    if (!await confirm({ title: 'Delete Brand', message: 'Are you sure you want to delete this brand? This will also remove all associated data. This cannot be undone.', confirmText: 'Delete Brand', severity: 'danger' })) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await brandService.deleteBrand(brandId);
      setBrands(brands.filter(b => b.id !== brandId));
      toast.success('Brand deleted successfully');
      
    } catch (err) {
      console.error('Error deleting brand:', err);
      setError(err.response?.data?.message || 'Failed to delete brand');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString || dateString === '1970-01-01 00:00:00') return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'healthy':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'inactive':
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
      case 'healthy':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'inactive':
      case 'error':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
    }
  };
  
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return theme.palette.error.main;
      case 'editor':
        return theme.palette.primary.main;
      case 'user':
      case 'viewer':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                Admin Panel
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage users, brands, and system settings
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeTab === 0 && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      fetchUsers();
                      fetchBrands();
                      fetchAllUsers();
                    }}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    Refresh
                  </Button>
                  {hasSlack && <Button
                    variant="outlined"
                    startIcon={<ChatIcon />}
                    onClick={() => setShowLinkForm(!showLinkForm)}
                    sx={{ borderRadius: 2 }}
                  >
                    Link Slack User
                  </Button>}
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => handleOpenUserDialog()}
                    sx={{
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                    }}
                  >
                    Add Web User
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>
        
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}

        {activeTab === 0 && allUsersError && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAllUsersError('')}>
              {allUsersError}
            </Alert>
          </Grid>
        )}

        {activeTab === 0 && hiddenUsersError && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setHiddenUsersError('')}>
              {hiddenUsersError}
            </Alert>
          </Grid>
        )}

        {activeTab === 0 && slackChecked && !hasSlack && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Slack isn't connected for your company, so Slack users can't be linked yet. Connect Slack on
              the <RouterLink to="/connectors">Connectors page</RouterLink> to link them.
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Card 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 120,
                    px: 3,
                    py: 2
                  }
                }}
              >
                <Tab label="Users" icon={<PeopleIcon />} iconPosition="start" />
                <Tab label="Company" icon={<SettingsIcon />} iconPosition="start" />
                <Tab label="Country Mapping" icon={<PublicIcon />} iconPosition="start" sx={!isSuperAdmin ? { display: 'none' } : undefined} />
                <Tab label="Call Centre" icon={<PhoneIcon />} iconPosition="start" sx={!canSeeCallCentreTab ? { display: 'none' } : undefined} />
                <Tab label="Debug Logs" icon={<BugReportIcon />} iconPosition="start" sx={!isSuperAdmin ? { display: 'none' } : undefined} />
                <Tab label="AI Agents" icon={<SmartToyIcon />} iconPosition="start" sx={!canSeeAIAgentsTab ? { display: 'none' } : undefined} />
                <Tab label={`Registrations${pendingRegistrations.length > 0 ? ` (${pendingRegistrations.length})` : ''}`} icon={<HowToRegIcon />} iconPosition="start" sx={!isSuperAdmin ? { display: 'none' } : undefined} />
                {user && Number(user.company_id) === SUPER_ADMIN_COMPANY_ID && !!user.isCompanyAdmin && (
                  <Tab label="Companies" icon={<BusinessIcon />} iconPosition="start" />
                )}
                <Tab label="CIN7 Port Config" icon={<MapIcon />} iconPosition="start" />
              </Tabs>
            </Box>
            
            {/* Users Tab — Unified (Platform + Slack) */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                {/* Link Slack User Form (collapsible) */}
                {showLinkForm && (
                  <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Link Slack User
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        label="Slack User Email"
                        value={searchEmail}
                        onChange={(e) => { setSearchEmail(e.target.value); setFoundSlackUser(null); }}
                        placeholder="user@example.com"
                        onKeyPress={(e) => { if (e.key === 'Enter') handleSearchSlackUser(); }}
                        sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleSearchSlackUser}
                        disabled={loading || !searchEmail}
                        sx={{ borderRadius: 2 }}
                      >
                        Search
                      </Button>
                      <Button variant="text" onClick={() => { setShowLinkForm(false); setSearchEmail(''); setFoundSlackUser(null); }}>
                        Cancel
                      </Button>
                    </Box>

                    {/* Show found user details before linking */}
                    {foundSlackUser && (
                      <Box sx={{ mt: 2 }}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: `${theme.palette.success.main}08`, border: `1px solid ${theme.palette.success.main}40`, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>User Found in Slack</Typography>
                          </Box>
                          <Typography variant="body2"><strong>Name:</strong> {foundSlackUser.name}</Typography>
                          <Typography variant="body2"><strong>Email:</strong> {foundSlackUser.email}</Typography>
                          <Typography variant="body2"><strong>Slack ID:</strong> {foundSlackUser.slackUserId}</Typography>
                        </Paper>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Permission Mode</InputLabel>
                            <Select
                              value={linkPermissionMode}
                              label="Permission Mode"
                              onChange={(e) => setLinkPermissionMode(e.target.value)}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="full_access">Full Access</MenuItem>
                              <MenuItem value="restricted">Restricted</MenuItem>
                              <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            onClick={handleLinkSlackUser}
                            disabled={loading}
                            sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #4A154B 30%, #611f69 90%)' }}
                          >
                            {loading ? <CircularProgress size={20} /> : 'Link User'}
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {/* Quick link without preview */}
                    {!foundSlackUser && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel>Permission Mode</InputLabel>
                          <Select
                            value={linkPermissionMode}
                            label="Permission Mode"
                            onChange={(e) => setLinkPermissionMode(e.target.value)}
                            sx={{ borderRadius: 2 }}
                          >
                            <MenuItem value="full_access">Full Access</MenuItem>
                            <MenuItem value="restricted">Restricted</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          onClick={handleLinkSlackUser}
                          disabled={loading || !searchEmail}
                          sx={{ borderRadius: 2, background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)' }}
                        >
                          {loading ? <CircularProgress size={20} /> : 'Find & Link in One Step'}
                        </Button>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption" component="div">
                        <strong>How linking works:</strong>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
                          <li>Enter a Slack user's email and click Search to preview</li>
                          <li>If a platform user with that email exists, they'll be auto-linked</li>
                          <li>If no platform user exists, they'll be added as "Slack-only" user</li>
                          <li>Or use "Find & Link in One Step" to skip the preview</li>
                        </ul>
                      </Typography>
                    </Alert>

                    <Box sx={{ mt: 2, p: 2, bgcolor: `${theme.palette.grey[50]}`, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" component="div">
                        <strong>Permission Modes:</strong>
                        <br />• <strong>Full Access</strong> — All brands, agents, and templates
                        <br />• <strong>Restricted</strong> — Only explicitly granted brands, agents, and templates
                        <br />• <strong>Admin</strong> — Full access + manage other users via /scm-admin commands
                      </Typography>
                    </Box>
                  </Paper>
                )}

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!loading && allUsers.length === 0 && users.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>No users found</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add a web user or link a Slack user to get started.
                    </Typography>
                  </Box>
                ) : !loading && (
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 650 }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Brands</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Agents</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allUsers.map((u) => {
                          const hasWebAccess = u.accessType === 'both' || u.accessType === 'web';
                          const isSlackOnly = u.accessType === 'slack';
                          const hasPermissions = (u.brandCount > 0 || u.agentCount > 0) || u.permissionMode === 'full_access' || u.permissionMode === 'admin';
                          return (
                          <TableRow
                            key={u.id}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              opacity: (isSlackOnly && !hasPermissions) ? 0.6 : 1
                            }}
                          >
                            <TableCell component="th" scope="row">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* Presence dot — always show for Slack users */}
                                {u.slackUserId && (
                                  <Box sx={{
                                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: u.presence === 'active' ? theme.palette.success.main : theme.palette.grey[400]
                                  }} />
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.name}</Typography>
                                {u.isCallCentreAgent === 1 && (
                                  <Chip label="Call Centre" size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={u.accessType === 'both' ? 'Web + Slack' : u.accessType === 'web' ? 'Web' : 'Slack Only'}
                                size="small"
                                variant={isSlackOnly && !hasPermissions ? 'outlined' : 'filled'}
                                sx={{
                                  bgcolor: (isSlackOnly && !hasPermissions) ? 'transparent' : `${getAccessTypeColor(u.accessType)}15`,
                                  color: getAccessTypeColor(u.accessType),
                                  fontWeight: 'medium'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {u.brandCount > 0 ? (
                                <Chip label={u.brandCount} size="small" sx={{ bgcolor: `${theme.palette.primary.main}15`, color: theme.palette.primary.main, fontWeight: 'medium' }} />
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.agentCount > 0 ? (
                                <Chip label={u.agentCount} size="small" sx={{ bgcolor: `${theme.palette.primary.main}15`, color: theme.palette.primary.main, fontWeight: 'medium' }} />
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {/* Manage Permissions — for any user (Slack or web-only) */}
                                {(u.slackUserId || u.platformUserId > 0) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenSlackPermissions(u)}
                                    sx={{ color: theme.palette.primary.main }}
                                    title="Manage Permissions (Brands, Agents, Sections)"
                                  >
                                    <VpnKeyIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {/* Edit platform account — for web users */}
                                {u.platformUserId > 0 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const platformUser = users.find(pu => pu.id === Number(u.platformUserId));
                                      if (platformUser) {
                                        handleOpenUserDialog(platformUser);
                                      } else {
                                        handleOpenUserDialog({
                                          id: Number(u.platformUserId),
                                          fullName: u.name,
                                          full_name: u.name,
                                          email: u.email,
                                          role: u.role
                                        });
                                      }
                                    }}
                                    title="Edit Account"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {/* Invite to Web — sends email invitation */}
                                {isSlackOnly && !u.platformUserId && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={async () => {
                                      try {
                                        await api.post('/api/users/invite', {
                                          email: u.email,
                                          fullName: u.name,
                                          role: 'user'
                                        });
                                        alert(`Invitation sent to ${u.email}`);
                                        fetchAllUsers();
                                      } catch (err) {
                                        alert(err.response?.data?.message || 'Failed to send invitation');
                                      }
                                    }}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                                  >
                                    Invite to Web
                                  </Button>
                                )}
                                {/* Remove user — revoke all permissions */}
                                {u.slackUserId && hasPermissions && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveUserPermissions(u)}
                                    sx={{ color: theme.palette.error.main }}
                                    title="Remove all permissions"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {/* Hide user from list — for slack-only without permissions */}
                                {isSlackOnly && !hasPermissions && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleHideUser(u.slackUserId, u.name)}
                                    sx={{ color: theme.palette.text.disabled }}
                                    title="Hide from Users list"
                                  >
                                    <VisibilityOffIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Hidden users section */}
                {hiddenUsers.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={showHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      onClick={() => setShowHidden(!showHidden)}
                      sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
                    >
                      {showHidden ? 'Hide' : 'Show'} {hiddenUsers.length} hidden user{hiddenUsers.length !== 1 ? 's' : ''}
                    </Button>
                    {showHidden && (
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableBody>
                            {hiddenUsers.map(hu => (
                              <TableRow key={hu.slackUserId} sx={{ opacity: 0.6 }}>
                                <TableCell>{hu.name}</TableCell>
                                <TableCell>{hu.email}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleUnhideUser(hu.slackUserId, hu.name)}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                  >
                                    Restore
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Company Settings Tab */}
            {activeTab === 1 && (
              <CompanySettingsTab />
            )}

            {/* Country Mapping Tab */}
            {activeTab === 2 && isSuperAdmin && (
              <CountryMappingTab />
            )}

            {/* Call Centre Phone Numbers Tab */}
            {activeTab === 3 && canSeeCallCentreTab && (
              <Box sx={{ p: 3 }}>
                <CallCentrePhoneNumbersTab />
              </Box>
            )}

            {/* Debug Logs Tab */}
            {activeTab === 4 && isSuperAdmin && (
              <Box sx={{ p: 3 }}>
                <DebugLogsTab />
              </Box>
            )}

            {/* AI Agents Tab */}
            {activeTab === 5 && canSeeAIAgentsTab && (
              <AIAgentsTab />
            )}

            {/* Pending Registrations Tab */}
            {activeTab === 6 && isSuperAdmin && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Pending Company Registrations</Typography>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPendingRegistrations} disabled={pendingLoading}>
                    Refresh
                  </Button>
                </Box>
                {pendingError && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPendingError('')}>
                    {pendingError}
                  </Alert>
                )}
                {pendingLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : pendingRegistrations.length === 0 ? (
                  // an empty list after a failed load is not "none pending": the warning above says so
                  pendingError ? null : <Alert severity="info">No pending registrations.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Company</strong></TableCell>
                          <TableCell><strong>Domain</strong></TableCell>
                          <TableCell><strong>Contact</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Registered</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingRegistrations.map((reg) => (
                          <TableRow key={reg.company_id}>
                            <TableCell>{reg.company_name}</TableCell>
                            <TableCell>{reg.domain}</TableCell>
                            <TableCell>{reg.full_name}</TableCell>
                            <TableCell>{reg.email}</TableCell>
                            <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleApproveCompany(reg.company_id)}
                                sx={{ mr: 1 }}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => { setRejectCompanyId(reg.company_id); setError(''); setRejectDialogOpen(true); }}
                              >
                                Reject
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Companies Tab (super-admin only) */}
            {activeTab === 7 && user && Number(user.company_id) === SUPER_ADMIN_COMPANY_ID && !!user.isCompanyAdmin && (
              <Box sx={{ p: 3 }}>
                <CompanyModulesTab />
              </Box>
            )}

            {/* CIN7 Port Config Tab (index depends on whether super-admin Companies tab is present) */}
            {((user && Number(user.company_id) === SUPER_ADMIN_COMPANY_ID && !!user.isCompanyAdmin) ? activeTab === 8 : activeTab === 7) && (
              <Box sx={{ p: 3 }}>
                <Cin7PortConfigAdmin />
              </Box>
            )}
          </Card>

          {/* Reject Registration Dialog */}
          <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogContent>
              {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
              <Typography variant="body2" sx={{ mb: 2 }}>Optionally provide a reason (will be emailed to the applicant):</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRejectCompany} color="error" variant="contained">Reject</Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
      
      {/* Create/Edit User Dialog */}
      <Dialog
        open={openUserDialog}
        onClose={handleCloseUserDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <form onSubmit={handleCreateUser}>
          <DialogTitle>
            {selectedUser ? 'Edit User' : 'Create New User'}
          </DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={userForm.fullName}
                  onChange={handleUserFormChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={userForm.email}
                  onChange={handleUserFormChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="role-select-label">Role</InputLabel>
                  <Select
                    labelId="role-select-label"
                    name="role"
                    value={userForm.role}
                    label="Role"
                    onChange={handleUserFormChange}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={selectedUser ? "New Password (leave blank to keep current)" : "Password"}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={userForm.password}
                  onChange={handleUserFormChange}
                  required={!selectedUser}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={toggleShowPassword} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              {!selectedUser && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={userForm.confirmPassword}
                    onChange={handleUserFormChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseUserDialog} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
              }}
            >
              {loading ? <CircularProgress size={20} /> : (selectedUser ? 'Update User' : 'Create User')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Create/Edit Brand Dialog */}
      <Dialog
        open={openBrandDialog}
        onClose={handleCloseBrandDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <form onSubmit={handleCreateBrand}>
          <DialogTitle>
            {selectedBrand ? 'Edit Brand' : 'Create New Brand'}
          </DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  name="name"
                  value={brandForm.name}
                  onChange={handleBrandFormChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={brandForm.description}
                  onChange={handleBrandFormChange}
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseBrandDialog} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
              }}
            >
              {loading ? <CircularProgress size={20} /> : (selectedBrand ? 'Update Brand' : 'Create Brand')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Permissions Dialog (unified — Brands, Agents, Sections, Data Access) */}
      <PermissionsDialog
        open={slackPermDialogOpen}
        onClose={() => { setSlackPermDialogOpen(false); setSlackPermUser(null); }}
        user={slackPermUser}
        teamId={teamId}
        onPermissionsUpdated={() => fetchAllUsers()}
        reloadError={allUsersError}
      />
      <ConfirmDialog {...confirmDialogProps} />


    </Box>
  );
};

export default AdminPanel;